import React from 'react';

import {EntitySummary} from 'constants/UserConstants';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';

import {BioentitySummaryGraphQL} from '../../graphql-fragments/registry/registry-items/__generated/BioentitySummaryGraphQL';

type BioentityAliasInfoNode = BioentitySummaryGraphQL['bioentityAliasInfos'][number];

/**
 * Gets the registry identifier (alias) for a specific collection
 * @param {Array<BioentityAliasInfoNode>} bioentityAliasInfos - Array of bioentity alias info nodes to search
 * @param {string | null | undefined} collection - The collection ID to find the identifier for
 * @returns {string | null} The registry identifier alias for the collection, or null if not found
 */
export function getRegistryIdentifier(
  bioentityAliasInfos: Array<BioentityAliasInfoNode>,
  collection: string | null | undefined
): string | null {
  const bioentityAliasInfo = bioentityAliasInfos.find((bai) => {
    const owner: EntitySummary = bai.registryCollectionOwnerJson;
    if (owner) {
      return owner.id === collection;
    }
    return false;
  });
  return bioentityAliasInfo ? bioentityAliasInfo.alias : null;
}

/**
 * Returns the BioentityAliasInfos which are not owned by the Registry, i.e. no registry collection owner
 *
 * @param {{bioentityAliasInfos: Array<BioentityAliasInfoNode>}} bioentity - The bioentity to filter alias infos from
 * @returns {Array<BioentityAliasInfoNode>} Array of BioentityAliasInfoNode that have no registry collection owner
 */
// Keep in sync with non_registry_aliases in
// benchling/notebook/structured_tables/resync/bioentity_live_data/bioentity_live_data.py
export function getNonRegistryIdentifierAliasInfos(bioentity: {
  bioentityAliasInfos: Array<BioentityAliasInfoNode>;
}): Array<BioentityAliasInfoNode> {
  return bioentity.bioentityAliasInfos.filter(
    ({registryCollectionOwnerJson}) => registryCollectionOwnerJson == null
  );
}

/**
 * Gets the primary registry identifier for a bioentity
 * Prioritizes organization registry identifier over personal collection identifier
 * @param {BioentitySummaryGraphQL} bioentity - The bioentity to get the primary identifier for
 * @returns {string | null} The primary registry identifier, or null if the bioentity is not registered
 */
export function getPrimaryRegistryIdentifier(bioentity: BioentitySummaryGraphQL): string | null {
  const registryIdentifiers = bioentity.bioentityAliasInfos.filter(
    (bioentityAliasInfo) => bioentityAliasInfo.registryCollectionOwnerJson != null
  );
  if (registryIdentifiers.length === 0) {
    return null;
  }
  const orgOwnerId = bioentity.folder.source.ownerJson.id;

  if (orgOwnerId) {
    const orgRegistryIdentifier = getRegistryIdentifier(registryIdentifiers, orgOwnerId);
    if (orgRegistryIdentifier) {
      return orgRegistryIdentifier;
    }
  }

  const currentUser = EntityStore.currentUser();
  if (currentUser) {
    return getRegistryIdentifier(registryIdentifiers, currentUser.id);
  }
  return null;
}

/**
 * Creates JSX components for displaying bioentity aliases with appropriate styling
 * @param {Array<BioentityAliasInfoNode>} aliases - Array of bioentity alias info nodes to display
 * @param {string | null} name - The primary name of the bioentity
 * @param {string | null | undefined} collection - The collection ID to identify the registry identifier
 * @returns {Array<JSX.Element>} Array of styled JSX span elements for rendering aliases
 */
export function getAliasComponents(
  aliases: Array<BioentityAliasInfoNode>,
  name: string | null,
  collection: string | null | undefined
): Array<JSX.Element> {
  let registryIdentifier: string | null;
  const components = [];
  if (name != null) {
    components.push(
      <span
        key={`name-${name}`}
        className="sidepanel-primaryLabel"
        {...getTestProps({component: 'Sidepanel', element: 'item', key: 'primaryLabel'})}
        data-amp-safe={false}
      >
        {`${name} `}
      </span>
    );
  }
  if (collection != null) {
    registryIdentifier = getRegistryIdentifier(aliases, collection);
    if (registryIdentifier != null) {
      components.push(
        <span key={`registryIdentifier-${registryIdentifier}`} className="sidepanel-secondaryLabel">
          {`${registryIdentifier} `}
        </span>
      );
    }
  }
  return components.concat(
    aliases
      .filter((a) => a.alias !== registryIdentifier && a.alias !== name)
      .map((alias) => (
        <span key={alias.alias} className="sidepanel-tertiaryLabel">
          {`${alias.alias} `}
        </span>
      ))
  );
}
